import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { Shield, ShieldCheck, ShieldOff, Key, Copy, RefreshCw, AlertTriangle, CheckCircle, Smartphone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function TwoFactorSetup() {
  const { user, token, refreshUser, getAdminDashboardUrl } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [disableForm, setDisableForm] = useState({ password: '', totp_code: '' });
  
  // Check if this is mandatory setup (came from /staff/setup-2fa)
  const isMandatorySetup = location.pathname === '/staff/setup-2fa';

  const is2FAEnabled = user?.two_fa_enabled;

  // Helper to get auth headers from context (not localStorage directly)
  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const initiate2FASetup = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/2fa/setup`, 
        {}, 
        { headers: getAuthHeaders() }
      );
      setSetupData(response.data);
      setShowSetupDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate 2FA setup');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Auto-start setup if mandatory
  useEffect(() => {
    if (isMandatorySetup && !is2FAEnabled && user) {
      initiate2FASetup();
    }
  }, [isMandatorySetup, is2FAEnabled, user, initiate2FASetup]);

  const verify2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/2fa/verify`,
        { totp_code: verifyCode },
        { headers: getAuthHeaders() }
      );
      toast.success('2FA enabled successfully!');
      setBackupCodes(response.data.backup_codes || setupData?.backup_codes || []);
      setShowSetupDialog(false);
      setShowBackupCodes(true);
      setVerifyCode('');
      await refreshUser();
      
      // If mandatory setup, redirect after showing backup codes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodesAcknowledged = () => {
    setShowBackupCodes(false);
    if (isMandatorySetup) {
      // Redirect to admin dashboard after mandatory setup
      const dashboard = getAdminDashboardUrl(user?.admin_role);
      toast.success('2FA setup complete! Redirecting to dashboard...');
      navigate(dashboard, { replace: true });
    }
  };

  const disable2FA = async () => {
    if (!disableForm.password || !disableForm.totp_code) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/2fa/disable`,
        disableForm,
        { headers: getAuthHeaders() }
      );
      toast.success('2FA disabled successfully');
      setShowDisableDialog(false);
      setDisableForm({ password: '', totp_code: '' });
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackupCodes = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/auth/2fa/backup-codes`, 
        { headers: getAuthHeaders() }
      );
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch backup codes');
    }
  };

  const regenerateBackupCodes = async () => {
    const code = prompt('Enter your current 2FA code to regenerate backup codes:');
    if (!code) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/2fa/regenerate-backup-codes`,
        { totp_code: code },
        { headers: getAuthHeaders() }
      );
      setBackupCodes(response.data.backup_codes);
      toast.success('Backup codes regenerated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate backup codes');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2 rounded-lg bg-primary/10">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Two-Factor Authentication</h1>
            <p className="text-muted-foreground">Secure your admin account with 2FA</p>
          </div>
        </div>

        {/* Status Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {is2FAEnabled ? (
                  <ShieldCheck className="w-8 h-8 text-green-500" />
                ) : (
                  <ShieldOff className="w-8 h-8 text-yellow-500" />
                )}
                <div>
                  <CardTitle>2FA Status</CardTitle>
                  <CardDescription>
                    {is2FAEnabled ? 'Your account is protected with 2FA' : '2FA is not enabled'}
                  </CardDescription>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                is2FAEnabled 
                  ? 'bg-green-500/10 text-green-500' 
                  : 'bg-yellow-500/10 text-yellow-500'
              }`}>
                {is2FAEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {!is2FAEnabled && (
              <Alert className="mb-4 border-yellow-500/50 bg-yellow-500/10">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                <AlertDescription className="text-yellow-200">
                  2FA is required for admin access. Please enable it to access admin features.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3">
              {is2FAEnabled ? (
                <>
                  <Button variant="outline" onClick={fetchBackupCodes} data-testid="view-backup-codes-btn">
                    <Key className="w-4 h-4 mr-2" />
                    View Backup Codes
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => setShowDisableDialog(true)}
                    data-testid="disable-2fa-btn"
                  >
                    <ShieldOff className="w-4 h-4 mr-2" />
                    Disable 2FA
                  </Button>
                </>
              ) : (
                <Button onClick={initiate2FASetup} disabled={loading} data-testid="enable-2fa-btn">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  {loading ? 'Setting up...' : 'Enable 2FA'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">How 2FA Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">Download an Authenticator App</p>
                <p>Use Google Authenticator, Authy, or any TOTP-compatible app.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Key className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">Scan QR Code</p>
                <p>Scan the QR code with your authenticator app to link your account.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">Enter Code on Login</p>
                <p>After entering your password, you'll need to provide a 6-digit code from your app.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Setup Dialog */}
        <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Set Up 2FA</DialogTitle>
              <DialogDescription>
                Scan the QR code with your authenticator app, then enter the verification code.
              </DialogDescription>
            </DialogHeader>

            {setupData && (
              <div className="space-y-4 py-4">
                {/* QR Code */}
                <div className="flex justify-center">
                  <div className="p-4 bg-white rounded-lg">
                    <img 
                      src={`${API_URL}/api/auth/2fa/qr-code`} 
                      alt="2FA QR Code" 
                      className="w-48 h-48"
                    />
                  </div>
                </div>

                {/* Manual Entry */}
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Or enter this code manually:</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="px-3 py-2 bg-muted rounded text-sm font-mono">
                      {setupData.secret}
                    </code>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => copyToClipboard(setupData.secret)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Verification Input */}
                <div className="space-y-2">
                  <Label htmlFor="verify-code">Verification Code</Label>
                  <Input
                    id="verify-code"
                    placeholder="Enter 6-digit code"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="text-center text-2xl tracking-widest"
                    data-testid="2fa-verify-input"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSetupDialog(false)}>
                Cancel
              </Button>
              <Button onClick={verify2FA} disabled={loading || verifyCode.length !== 6} data-testid="verify-2fa-btn">
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Backup Codes Dialog */}
        <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Backup Codes
              </DialogTitle>
              <DialogDescription>
                Save these codes somewhere safe. Each code can only be used once.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="grid grid-cols-2 gap-2 mb-4">
                {backupCodes.map((code) => (
                  <div 
                    key={code}
                    className="px-3 py-2 bg-muted rounded text-center font-mono text-sm cursor-pointer hover:bg-muted/80"
                    onClick={() => copyToClipboard(code)}
                  >
                    {code}
                  </div>
                ))}
              </div>
              
              <div className="flex justify-between">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => copyToClipboard(backupCodes.join('\n'))}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={regenerateBackupCodes}
                  disabled={loading}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleBackupCodesAcknowledged}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Disable Dialog */}
        <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <ShieldOff className="w-5 h-5" />
                Disable 2FA
              </DialogTitle>
              <DialogDescription>
                Enter your password and current 2FA code to disable two-factor authentication.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="disable-password">Password</Label>
                <Input
                  id="disable-password"
                  type="password"
                  value={disableForm.password}
                  onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })}
                  data-testid="disable-2fa-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="disable-code">2FA Code</Label>
                <Input
                  id="disable-code"
                  placeholder="6-digit code"
                  value={disableForm.totp_code}
                  onChange={(e) => setDisableForm({ ...disableForm, totp_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  maxLength={6}
                  data-testid="disable-2fa-code"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDisableDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={disable2FA} 
                disabled={loading}
                data-testid="confirm-disable-2fa-btn"
              >
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
